import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { AngleUnderline } from './angle-underline.js';
import { useLocale } from '../../contexts/LocaleContext';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../ui/button';
import { trackCtaClicked } from '../../utils/analytics';
import { GooglePlayBadge } from './GooglePlayBadge';
import { GoToMyDeskLabel } from './GoToMyDeskLabel';

import writerMan from '../../assets/landing/writer_man.webp';
import writerWoman from '../../assets/landing/writer_woman.webp';

const HERO_IMAGES = [writerMan, writerWoman] as const;

export const LANDING_ROTATING_WORD_KEYS = [
  'landing_rotating_word_novelists',
  'landing_rotating_word_screenwriters',
  'landing_rotating_word_editors',
  'landing_rotating_word_journalists',
  'landing_rotating_word_proofreaders',
  'landing_rotating_word_storytellers',
] as const;

const HERO_CTA_CLASSNAME =
  'rounded-[10px] py-[11px] px-5 h-auto text-[14px] bg-[var(--scribane-green)] text-[var(--scribane-black)] hover:bg-[var(--scribane-black)] hover:text-[var(--scribane-green)] transition-[background,color] duration-[220ms]';

export const Hero: React.FC = () => {
  const { t } = useLocale();
  const { token } = useAuth();
  const [heroImage] = useState<string>(
    () => HERO_IMAGES[Math.random() < 0.5 ? 0 : 1] ?? writerMan,
  );

  const otherRotatingWords = useMemo(
    () =>
      LANDING_ROTATING_WORD_KEYS.map((key) => t(key).toUpperCase()),
    [t],
  );

  const title = t('landing_hero_title');
  const titleParts = title.trim().split(/\s+/);
  const baseWord = titleParts.at(-1) ?? title;
  const titlePrefix = titleParts.slice(0, -1).join(' ');

  const baseWordUpper = useMemo(() => baseWord.toUpperCase(), [baseWord]);
  const [animatedWord, setAnimatedWord] = useState<string>(baseWordUpper);
  useEffect(() => {
    let cancelled = false;

    const sleep = async (ms: number): Promise<void> => {
      await new Promise<void>((resolve) => {
        window.setTimeout(() => resolve(), ms);
      });
    };

    const run = async (): Promise<void> => {
      setAnimatedWord(baseWordUpper);

      await sleep(2000);

      let current = baseWordUpper;
      const words = otherRotatingWords;
      const baseWordIndex = words.findIndex((w) => w === baseWordUpper);
      let nextIndex = baseWordIndex === 0 ? 1 : 0;

      while (!cancelled) {
        for (let i = current.length; i >= 0; i -= 1) {
          if (cancelled) return;
          setAnimatedWord(current.slice(0, i));
          await sleep(55);
        }

        const next = words[nextIndex] ?? baseWordUpper;
        for (let i = 0; i <= next.length; i += 1) {
          if (cancelled) return;
          setAnimatedWord(next.slice(0, i));
          await sleep(80);
        }

        current = next;
        nextIndex = (nextIndex + 1) % words.length;

        await sleep(2000);
      }
    };

    void run();

    return () => {
      cancelled = true;
    };
  }, [baseWordUpper, otherRotatingWords]);

  return (
    <section
      id="hero"
      className="relative min-h-[85vh] sm:min-h-screen scroll-mt-24 flex items-center justify-center px-8 bg-cover bg-no-repeat bg-[60%_center] sm:bg-center"
      style={{ backgroundImage: `url(${heroImage})` }}
    >
      <div
        className="hero-gradient-overlay pointer-events-none absolute inset-0"
        aria-hidden="true"
      />
      <div className="relative z-10 max-w-7xl mx-auto text-center -mt-16 sm:-mt-20 lg:-mt-24">
        <h1
          className="text-3xl sm:text-5xl lg:text-6xl xl:text-7xl font-normal text-black mb-12 sm:mb-16 lg:mb-20 uppercase leading-[1.05] tracking-[0.1em] sm:tracking-[0.15em] lg:tracking-[0.2em]"
          style={{ fontFamily: "'DM Mono', monospace" }}
        >
          {titlePrefix ? (
            <span className="block">{titlePrefix}</span>
          ) : null}

          <span className="relative inline-block">
            <span className="relative z-10">{animatedWord}</span>

            <AngleUnderline className="left-[0.08em] right-[0.08em] -bottom-3 h-4" />

            <span
              aria-hidden="true"
              className="absolute left-full ml-2 inline-block w-[0.14em] bg-[var(--scribane-green)] animate-[scribane-caret_1s_steps(1)_infinite]"
              style={{ height: '0.95em', bottom: '0.06em' }}
            />
          </span>
        </h1>
        <div className="mx-12 sm:mx-6 lg:mx-8 mb-12">
          <p
            className="text-xl sm:text-2xl lg:text-3xl text-[var(--scribane-black)] max-w-3xl mx-auto"
            dangerouslySetInnerHTML={{ __html: t('landing_hero_subtitle') }}
          />
          <p className="mt-5 text-sm sm:text-base text-[var(--scribane-black)]/70 max-w-2xl mx-auto tracking-wide">
            {t('landing_hero_description')}
          </p>
        </div>
        <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
          {token ? (
            <Button asChild className={HERO_CTA_CLASSNAME}>
              <Link
                to="/desk"
                className="inline-flex items-center gap-2"
                onClick={() => trackCtaClicked(t('go_to_my_desk'), 'hero')}
              >
                <GoToMyDeskLabel />
              </Link>
            </Button>
          ) : (
            <Button
              onClick={() => {
                trackCtaClicked(t('landing_cta_hero'), 'hero');
                document.getElementById('cta')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }}
              className={HERO_CTA_CLASSNAME}
            >
              {t('landing_cta_hero')}
            </Button>
          )}
          <GooglePlayBadge
            height={44}
            onClick={() => trackCtaClicked(t('landing_apps_android_cta'), 'hero_play_store')}
          />
        </div>
      </div>
    </section>
  );
};
